import { Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { differenceInCalendarDays } from 'date-fns';
import { useEcommerceManagerFactory } from '@spotahome/ecommerce-tracking';
import {
  Button,
  useModalHandle,
  useUserInfoContext
} from '@spotahome/ui-library';
import { getDateAsUTC } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';
import ListingDatesValidatorService from '@spotahome/ui-library/src/tenant/components/utils/listing-dates-validator-service';
import { Mediator } from '@spotahome/soyuz-mediator';
import { trans } from '@spotahome/soyuz/client';
import Tracking from '@spotahome/soyuz-tracking';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import bookNowButtonShape from '../../shapes/bookNowButton';
import { NAVIGATE_CHECKOUT_FUNNEL } from '../../channels';
import MinimumStay from '../../LEGACY/javascripts/ui/ads/minimum-stay';
import EarlyBookingModal from '../EarlyBookingModal';
import './BookNowButton.scss';

const NOT_AVAILABLE_VALIDATION = 'notavailable';
const UNDEFINED_VALIDATION = 'undef';

const BookNowButton = ({
  bookingStatus,
  moveIn = '',
  moveOut = '',
  minStay,
  maxStay,
  moveInDatesRanges = [],
  bookingAcceptanceMaxDays,
  allowsPermanentContract,
  alt = false,
  listing,
  checkAvailabilityTransId = 'property_show.book_now_card.check_availability',
  href = '',
  instantBooking = {},
  onClickBookNowButton = () => {},
  onClickSuccessBookNowButton = () => {},
  unavailabilities = [],
  pricing,
  hasGAP
}) => {
  const ecommerceManager = useEcommerceManagerFactory({
    sendInHouseAnalytics: false
  });
  const { isUserLogged } = useUserInfoContext();

  const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();

  const isMoveInAYearFromNow =
    differenceInCalendarDays(
      getDateAsUTC(new Date(moveIn)),
      getDateAsUTC(new Date())
    ) >= 365;

  const isMinStayValid = ListingDatesValidatorService.isMinStayValid(
    moveIn,
    moveOut,
    minStay
  );
  const isMaxStayValid = ListingDatesValidatorService.isMaxStayValid(
    moveIn,
    moveOut,
    maxStay
  );
  const isMoveInDateValid = ListingDatesValidatorService.isMoveInDateValid(
    moveIn,
    moveInDatesRanges
  );

  const isDisabled =
    bookingStatus === NOT_AVAILABLE_VALIDATION ||
    bookingStatus === UNDEFINED_VALIDATION;

  const handleOnClick = useCallback(
    e => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      if (isDisabled) {
        return;
      }

      if (instantBooking.isEnabled) {
        Tracking.hotjar.triggerEvent('listing-IB-book-now-click');
      }

      if (isMoveInAYearFromNow) {
        handleModalOpen();
        return;
      }

      if (!moveIn && !moveOut) {
        SoyuzAnalytics.sendGA4Event('check_availabilities', {});
      }

      if (moveIn && (moveOut || allowsPermanentContract)) {
        ecommerceManager.trackCheckoutStep1({
          pricing: pricing.pricing,
          listing,
          unavailabilities
        });

        if (isUserLogged) {
          window.location = href;
        } else {
          onClickSuccessBookNowButton();
        }
      } else {
        onClickBookNowButton();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDisabled, moveIn, moveOut, pricing]
  );

  useEffect(() => {
    Mediator.subscribe(NAVIGATE_CHECKOUT_FUNNEL, handleOnClick);

    return () => Mediator.unsubscribe(NAVIGATE_CHECKOUT_FUNNEL, handleOnClick);
  }, [handleOnClick]);

  const buttonProps = {
    dataTest: 'bookNowButton',
    className: 'booknow-button',
    disabled: isDisabled,
    size: 'big-redesign',
    onClick: handleOnClick,
    alt
  };

  const showCheckAvailabilityButton = !moveIn && !moveOut;
  const showBookButton = listing.isBookable && (moveIn || moveOut);
  return (
    <>
      <div>
        {isDisabled && !hasGAP && (
          <div className="booknow-card__message">
            <strong>
              {trans(
                bookingStatus === NOT_AVAILABLE_VALIDATION
                  ? 'property_show.book_now_card.not_available'
                  : 'property_show.book_now_card.property_not_available'
              )}
            </strong>
            <MinimumStay
              moveIn={moveIn}
              moveOut={moveOut}
              bookingAcceptanceMaxDays={bookingAcceptanceMaxDays}
              minStay={minStay}
              maxStay={maxStay}
              minStayValid={isMinStayValid}
              maxStayValid={isMaxStayValid}
              isMoveInDateValid={isMoveInDateValid}
              moveInDatesRanges={moveInDatesRanges}
            />
          </div>
        )}

        {showCheckAvailabilityButton && (
          <Button
            {...buttonProps}
            renderContent={() => trans(checkAvailabilityTransId)}
          />
        )}

        {showBookButton && (
          <Fragment>
            <Button
              {...buttonProps}
              renderContent={() =>
                `${trans('property_show.book_now_card.request_continue')} >>`
              }
            />
            <div className="pay-nothing-now">
              {trans('listing.book-now-card.payment-safety')}
            </div>
          </Fragment>
        )}
      </div>

      <EarlyBookingModal
        onGoToFunnel={() => (window.location = href)}
        onCloseModal={handleModalClose}
        onOpenLoginModal={onClickSuccessBookNowButton}
        isModalOpen={isModalOpen}
        isUserLogged={isUserLogged}
      />
    </>
  );
};

BookNowButton.propTypes = PropTypes.shape(bookNowButtonShape).isRequired;

export default BookNowButton;
